/// <reference path="../types.d.ts" />
import { api } from '../core/net';
import { UserContext, Auth } from './action-types';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { clearToken, fetchConfig } from './_action-utilities';
import {
  loading,
  configAvailable,
  setActiveUser,
  loadingAction,
  setHomepagePreference
} from './ui-core.actions';
import { navigateContext } from './navigation.actions';
import { getEula, saveEula } from './eula-shared.actions';
import history from '../history';
import {
  authenticationContext,
  cmxLocation,
  HttpMethods,
  commonActionUtils,
  uiActions,
  launchPageActions,
  AmplifyCore,
  xcaliberActions,
  commonEnums
} from '@codametrix/ui-common';
import { saveServiceLine } from './service-lines';
import {
  broadcastOrgContextSwitched,
  getBootstrappedServiceLineId,
  patchSessionAttributes
} from '@codametrix/client-session';
import {
  getDefaultServiceLine,
  getHomepagePreference
} from '../core/preferences/preferences';
import { loadPreferences } from './user-preferences';
import { finalizeContextSwitchReload } from '../core/context-switch-reload';
const { shimContext } = commonActionUtils;

const displayContexts = createAction<CMxAPI.OrganizationContext[]>(
  UserContext.DISPLAY_CONTEXTS
);

const tokenAvailable = createAction(Auth.TOKEN_AVAILABLE, (payload: CMxCommonApp.BearerToken) => ({
  payload,
  meta: { broadcast: false }
}));

const shouldRefresh = createAction(Auth.SHOULD_REFRESH, () => ({
  payload: undefined as void,
  meta: { broadcast: true }
}));

const _getConfigs = async (dispatch: any) => {
  const config = await fetchConfig();

  dispatch(configAvailable(config));
};

// Guards against a request that never settles — e.g. CSRF/session hydration
// silently produced an unusable token and the request hangs rather than
// throwing. Without this, `loading` would stay true forever on a fresh tab.
const BOOTSTRAP_TIMEOUT_MS = 10000;

export class BootstrapTimeoutError extends Error {
  constructor() {
    super('Timed out waiting for /contexts/v1');
    this.name = 'BootstrapTimeoutError';
  }
}

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new BootstrapTimeoutError()), timeoutMs);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
};

const fetchContexts = async (dispatch: any) => {
  const bootstrap = await withTimeout(
    api<CMxAPI.ContextAPI>({
      endpoint: `/contexts/v1`
    }),
    BOOTSTRAP_TIMEOUT_MS
  );
  await _getConfigs(dispatch);
  return bootstrap;
};

const postContextChoiceNavigation = async (
  context: CMx.Context,
  dispatch: any,
  contextChoice?: CMx.JumpArgs,
  navigate: boolean = true
) => {
  const serviceLines = context.activeContext?.serviceLines || [];

  const userDefaults = await api<CMxAPI.Preference[]>({
    endpoint: `/userpreferences/${context.user.id}/v1`
  });

  const defaultServiceLineId = getDefaultServiceLine(userDefaults);
  const defaultServiceLine = serviceLines.find(
    sl => sl.id === defaultServiceLineId
  );

  const selectedServiceLine = serviceLines.find(
    sl => sl.id === contextChoice?.servicelineId
  );
  const homepagePreference = getHomepagePreference(userDefaults);
  dispatch(setHomepagePreference(homepagePreference));

  // No explicit choice (e.g. the sign-in -> app history.replace handoff, or the
  // reload that follows a context switch): prefer the service line the session
  // already recorded (read from the session-service-line-id meta tag at boot)
  // over the saved preference default and serviceLines[0]. The session
  // attribute holds the user's most recent explicit pick, so letting the
  // default win here would undo a service-line choice made in the switcher and
  // PATCH the session attribute back to the default below.
  const bootstrappedServiceLine = serviceLines.find(
    sl => sl.id === getBootstrappedServiceLineId()
  );

  const knownServiceLine =
    selectedServiceLine ?? bootstrappedServiceLine ?? defaultServiceLine;

  // Several service lines and nothing to go on (no explicit pick, session
  // record or saved default): leave the service line unset and send the user to
  // the chooser. Falling back to serviceLines[0] here would put them in that
  // line, and PATCH it onto the session, before they ever chose it. A
  // deep-link bootstrap (navigate === false) has no chooser to defer to, so it
  // still falls back below.
  if (navigate && serviceLines.length > 1 && !knownServiceLine) {
    history.push('/choose/service-line/');
    return;
  }

  const chosenServiceLine = knownServiceLine ?? serviceLines[0];
  dispatch(saveServiceLine(chosenServiceLine));

  // Resync the session attribute on boot when it's missing or stale (e.g. a
  // session established before this attribute existed, or via the legacy
  // dashboard SPA auth path that never called chooseServiceLine). A normal
  // refresh where the two already agree is a no-op -- no write on every load.
  if (
    chosenServiceLine &&
    getBootstrappedServiceLineId() !== chosenServiceLine.id
  ) {
    try {
      await patchSessionAttributes({ serviceLineId: chosenServiceLine.id });
    } catch {
      // Non-blocking: bootstrap must not fail over this.
    }
  }

  // A deep-link/direct-URL bootstrap is already on the page it wants — skip
  // the homepage/service-line-choice redirect below, which exists for the
  // login flow where there's no destination yet to preserve.
  if (!navigate) {
    return;
  }

  // Note: Case-details-layout configuration will be loaded on-demand when a case is opened
  const location = cmxLocation.getLocation();

  if (location === '/eula/') {
    const eulaUpToDate = await getEula(
      context.user.id || -1,
      context.activeContext?.organizationId || -1
    );
    dispatch(saveEula(eulaUpToDate));
    history.push(location);
  } else {
    dispatch(navigateContext({ context, homepagePreference }));
  }
};

// there are two actions here that have very similar names
// selectContext and chooseContext.  chooseContext informs the ui
// that a context choice has been made.
// selectContext is the api sequence to tell the server that a context
// has been made.
// This has been split into two calls so that context choice can be made
// *prior* to further api calls being made.
const selectContext = createAsyncThunk<
  CMx.Context,
  CMx.OrganizationFormChoice,
  { rejectValue: CMxCommonApp.SubmitError }
>(UserContext.CHOOSE_CONTEXT, async (contextChoice, { dispatch }) => {
  dispatch(loading(true));
  dispatch(loadingAction({ isLoading: true }));
  const oldToken = authenticationContext.getToken();
  const contexts = await api<CMxAPI.ContextAPI>({
    endpoint: `/contexts/v1`,
    init: {
      method: HttpMethods.PUT
    },
    body: contextChoice
  });

  const shimmed = shimContext(contexts);

  if (
    shimmed.activeContext &&
    (shimmed.activeContext.displayName === null ||
      shimmed.activeContext?.displayName === undefined)
  ) {
    shimmed.activeContext.displayName = shimmed.activeContext?.organizationName;
  }

  const token: CMxCommonApp.BearerToken = {
    token: `${shimmed.jwtToken}`,
    expiryTimeToLive: shimmed.expiryTimeToLive
  };
  if (oldToken && oldToken !== token.token) {
    await clearToken(oldToken);
  }
  dispatch(tokenAvailable(token));
  await dispatch(uiActions.chooseContext(shimmed));

  await postContextChoiceNavigation(shimmed, dispatch);

  dispatch(loading(false));
  dispatch(loadingAction({ isLoading: false }));
  return shimmed;
});

export const onlyRefreshContext = createAsyncThunk<
  void,
  CMx.JumpArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(UserContext.ONLY_REFRESH_CONTEXT, async (contextChoice, { dispatch }) => {
  const freshContexts = await api<CMxAPI.ContextAPI>({
    endpoint: `/contexts/v1`,
    init: {
      method: HttpMethods.PUT
    },
    body: { organizationId: contextChoice.organizationId }
  });

  const shimmed = shimContext(freshContexts);
  const token: CMxCommonApp.BearerToken = {
    token: `${shimmed.jwtToken}`,
    expiryTimeToLive: shimmed.expiryTimeToLive
  };

  dispatch(tokenAvailable(token));
  return;
});

const putContext = async (
  contextChoice: CMx.JumpArgs,
  dispatch: any
): Promise<CMx.Context> => {
  const oldToken = authenticationContext.getToken();
  const freshContexts = await api<CMxAPI.ContextAPI>({
    endpoint: `/contexts/v1`,
    init: {
      method: HttpMethods.PUT
    },
    body: { organizationId: contextChoice.organizationId }
  });

  dispatch(setActiveUser(freshContexts.user));
  dispatch(launchPageActions.userDetailsAvailable(freshContexts) as any);

  const shimmed = shimContext(freshContexts);
  const token: CMxCommonApp.BearerToken = {
    token: `${shimmed.jwtToken}`,
    expiryTimeToLive: shimmed.expiryTimeToLive
  };

  if (oldToken && oldToken !== token.token) {
    await clearToken(oldToken);
  }
  dispatch(tokenAvailable(token));
  await _getConfigs(dispatch);
  await dispatch(uiActions.chooseContext(shimmed));

  return shimmed;
};

const refreshContext = createAsyncThunk<
  void,
  CMx.JumpArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(UserContext.REFRESH_CONTEXT, async (contextChoice, { dispatch }) => {
  await putContext(contextChoice, dispatch);
});

const jumpContext = createAsyncThunk<
  CMx.Context,
  CMx.JumpArgs,
  { rejectValue: CMxCommonApp.SubmitError }
>(UserContext.JUMP_CONTEXT, async (contextChoice, { dispatch, getState }) => {
  dispatch(loading(true));
  // enable the new context
  const shimmed = await putContext(contextChoice, dispatch);
  if (shimmed.user.id) {
    dispatch(loadPreferences(shimmed.user.id));
  }
  // we are technically in the new context now, so we can stop "loading"
  // the awaitingResources state will keep the ui from displaying the new
  // context until the resources are loaded.
  dispatch(loading(false));

  // route the user to the appropriate page
  await postContextChoiceNavigation(shimmed, dispatch, contextChoice);

  // service line specific resources
  const { servicelineId } = contextChoice;
  const selectedServiceLine = shimmed.activeContext?.serviceLines?.find(
    serviceLine => serviceLine.id === servicelineId
  );
  const serviceLineName = selectedServiceLine
    ? selectedServiceLine.name
    : commonEnums.ServiceLines.ALL;

  // load provider directory if needed
  const hasProviderDirectory = contextChoice
    ? AmplifyCore.benchEditable.isResourceAllowed(
        (shimmed.activeContext as any) as CMxAPI.OrganizationContext,
        'PROVIDER_DIRECTORY',
        serviceLineName
      )
    : false;
  if (hasProviderDirectory) {
    await xcaliberActions.loadXCaliber(dispatch);
  }

  // Other tabs share this browser's session cookie, so the org they're showing is
  // now stale regardless of how each was opened — tell every tab to reinitialize.
  broadcastOrgContextSwitched();
  finalizeContextSwitchReload(getState);
  return shimmed;
});

export {
  fetchContexts,
  displayContexts,
  selectContext,
  putContext,
  shouldRefresh,
  refreshContext,
  jumpContext,
  tokenAvailable,
  postContextChoiceNavigation
};
